export type PrismColor = 'red' | 'yellow' | 'green' | 'blue' | 'violet';
export type SubStatType = 'gc_multi' | 'gc_bonus' | 'exp_bonus' | 'reward_multi' | 'hybrid_bonus' | 'reward_flat' | 'score_bonus' | 'fever_bonus' | 'time_bonus';

export const rollAnimaTicketStars = (ticketLevel: number): number => {
  const rand = Math.random() * 100;
  if (ticketLevel === 1) {
    if (rand < 95.0) return 1;
    if (rand < 99.9) return 2;
    return 3;
  }
  if (ticketLevel === 2) {
    if (rand < 95.0) return 2;
    return 3;
  }
  return 3;
};


export interface Potential {
  type: SubStatType;
  stars: number;
  value: number;
}

export interface BlackStats {
  boost: number;        // e.g., 0.50 for +50%
  penaltyType: string;  // e.g., 'extraTime'
  penaltyValue: number; // e.g., 0.20 for -20%
}

export interface Prism {
  id: string;
  color: PrismColor;
  stars: number;
  slot1: Potential;
  slot2?: Potential | null;
  slot3?: Potential | null;
  blackStats?: BlackStats | null;
  locked?: boolean;
}

export interface PrismBuffs {
  coinMultiplier: number;
  expMultiplier: number;
  scoreMultiplier: number;
  flatCoins: number;
  flatExp: number;
  extraTime: number;
  extraFever: number;
  heartSaveChance: number;
  feverScoreMultiplier: number;
}

export const SUB_STAT_POOL: SubStatType[] = [
  'gc_multi', 'exp_bonus', 'reward_multi', 'reward_flat', 'score_bonus', 'fever_bonus', 'time_bonus'
];

export const getBaseTraitValue = (color: PrismColor, stars: number) => {
  const table: Record<PrismColor, number[]> = {
    red:    [0, 1.05, 1.10, 1.15, 1.25],   // Fever Score Multiplier (1.05x, 1.10x...)
    yellow: [0, 1.05, 1.10, 1.25, 1.50], // GC Multiplier (1.05x, 1.10x...)
    green:  [0, 1.05, 1.10, 1.25, 1.50], // EXP Multiplier
    blue:   [0, 2.0, 5.0, 8.0, 12.0],  // Game Time Extension (+sec)
    violet: [0, 1.02, 1.05, 1.10, 1.20], // Final Score Bonus (1.02x, 1.05x...)
  };
  return table[color]?.[stars] || 0;
};

export const getPotentialValue = (type: SubStatType, stars: number) => {
  const values: Record<SubStatType, number> = {
    gc_multi: 5.0,
    gc_bonus: 5.0, // Legacy alias
    exp_bonus: 5.0,
    reward_multi: 3.0,
    hybrid_bonus: 3.0, // Legacy alias
    reward_flat: 30,
    score_bonus: 2.0,
    fever_bonus: 10.0,
    time_bonus: 2.0
  };
  return values[type] !== undefined ? values[type] : 0;
};

const rollPotential = (prismStars: number, forceExact: boolean = false, dropChance: number = 0.7): Potential => {
  const type = SUB_STAT_POOL[Math.floor(Math.random() * SUB_STAT_POOL.length)];
  let stars = prismStars;
  
  if (!forceExact) {
    // Standard: 70% chance to drop (30% match)
    // 3rd Slot: 85% chance to drop (15% match)
    if (Math.random() < dropChance && prismStars > 1) {
      stars = prismStars - 1;
    }
  }
  
  return {
    type,
    stars,
    value: getPotentialValue(type, stars)
  };
};

export const generatePrism = (stars: number, color?: PrismColor): Prism => {
  const colors: PrismColor[] = ['red', 'yellow', 'green', 'blue', 'violet'];
  const finalColor = color || colors[Math.floor(Math.random() * colors.length)];
  
  return {
    id: `prism_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`,
    color: finalColor,
    stars,
    slot1: rollPotential(stars, true),
    slot2: stars >= 2 ? rollPotential(stars, false, 0.7) : null,
    slot3: stars >= 3 ? rollPotential(stars, false, 0.9) : null
  };
};

export const evolvePrism = (p1: Prism, p2: Prism, p3: Prism): Prism => {
  const newStars = p1.stars + 1;
  
  return {
    id: `prism_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`,
    color: p1.color,
    stars: newStars,
    slot1: rollPotential(newStars, true),
    slot2: newStars >= 2 ? rollPotential(newStars, false, 0.7) : null,
    slot3: newStars >= 3 ? rollPotential(newStars, false, 0.9) : null,
    // Black stats carry over and scale (since they are multipliers/debuffs)
    blackStats: p1.blackStats || null
  };
};

export const rerollPrism = (prism: Prism, stars: number): Prism => {
  return {
    ...prism,
    slot1: rollPotential(stars, true),
    slot2: rollPotential(stars, false, 0.7),
    slot3: rollPotential(stars, false, 0.9)
  };
};

export const generateBlackStats = (): BlackStats => {
  const penaltyTypes = ['coinMultiplier', 'expMultiplier', 'scoreMultiplier', 'extraTime', 'extraFever'];
  return {
    boost: 0.2 + Math.random() * 0.8, // 20% to 100%
    penaltyType: penaltyTypes[Math.floor(Math.random() * penaltyTypes.length)],
    penaltyValue: 0.05 + Math.random() * 0.45 // 5% to 50%
  };
};

export const getPrismDescription = (prism: Prism) => {
  const traitValues: Record<PrismColor, string> = {
    red: `Fever Score +${Math.round((getBaseTraitValue(prism.color, prism.stars) - 1) * 100)} %`,
    yellow: `Coin Bonus +${Math.round((getBaseTraitValue(prism.color, prism.stars) - 1) * 100)} %`,
    green: `EXP Bonus +${Math.round((getBaseTraitValue(prism.color, prism.stars) - 1) * 100)} %`,
    blue: `Game Time +${getBaseTraitValue(prism.color, prism.stars)} s`,
    violet: `Score Bonus +${Math.round((getBaseTraitValue(prism.color, prism.stars) - 1) * 100)} %`
  };
  return traitValues[prism.color];
};

export const calculatePrismBuffs = (prism: Prism | null | undefined): PrismBuffs => {
  const result: PrismBuffs = {
    coinMultiplier: 0,
    expMultiplier: 0,
    scoreMultiplier: 0,
    flatCoins: 0,
    flatExp: 0,
    extraTime: 0,
    extraFever: 0,
    heartSaveChance: 0,
    feverScoreMultiplier: 0
  };

  if (!prism) return result;

  // 1. Base Trait
  let traitVal = getBaseTraitValue(prism.color, prism.stars);
  
  // Apply Black Boost if present (Positive Boost to Base Trait)
  if (prism.blackStats) {
      if (['red', 'yellow', 'green', 'violet'].includes(prism.color)) {
          // It's a multiplier-based trait: (Val - 1) is the actual bonus
          const bonus = traitVal - 1;
          const boostedBonus = bonus * (1 + prism.blackStats.boost);
          traitVal = 1 + boostedBonus;
      } else {
          // It's a flat-based trait (Fever/Time)
          traitVal = traitVal * (1 + prism.blackStats.boost);
      }
  }

  switch (prism.color) {
    case 'red': result.feverScoreMultiplier += (traitVal as number) - 1; break;
    case 'yellow': result.coinMultiplier += (traitVal as number) - 1; break;
    case 'green': result.expMultiplier += (traitVal as number) - 1; break;
    case 'blue': result.extraTime += traitVal as number; break;
    case 'violet': result.scoreMultiplier += (traitVal as number) - 1; break;
  }

  // 2. Potentials
  const pots = [prism.slot1, prism.slot2, prism.slot3].filter((p): p is Potential => !!p);
  pots.forEach(p => {
    switch (p.type) {
      case 'gc_multi':
      case 'gc_bonus': 
        result.coinMultiplier += p.value / 100; 
        break;
      case 'exp_bonus': 
        result.expMultiplier += p.value / 100; 
        break;
      case 'reward_multi': 
      case 'hybrid_bonus':
        result.coinMultiplier += p.value / 100; 
        result.expMultiplier += p.value / 100; 
        break;
      case 'reward_flat':
        result.flatCoins += p.value;
        result.flatExp += p.value;
        break;
      case 'score_bonus': result.scoreMultiplier += p.value / 100; break;
      case 'fever_bonus': result.feverScoreMultiplier += p.value / 100; break;
      case 'time_bonus': result.extraTime += p.value; break;
    }
  });

  // 3. Apply Black Penalty (if present)
  if (prism.blackStats) {
      const { penaltyType, penaltyValue } = prism.blackStats;
      const currentVal = (result as any)[penaltyType];
      
      if (penaltyType.includes('Multiplier')) {
          // Percentage-based stats: e.g., 0.5 (50%) -> reduce by 5-50%
          (result as any)[penaltyType] = currentVal * (1 - penaltyValue);
      } else {
          // Flat-based stats: reduce total by percentage
          (result as any)[penaltyType] = currentVal * (1 - penaltyValue);
      }
  }

  return result;
};

export const getPotentialDescription = (pot: Potential) => {
  switch (pot.type) {
    case 'gc_multi':
    case 'gc_bonus': 
      return `Coin Bonus +${pot.value} %`;
    case 'exp_bonus': 
      return `EXP Bonus +${pot.value} %`;
    case 'reward_multi':
    case 'hybrid_bonus': 
      return `Coin \u0026 EXP +${pot.value} %`;
    case 'reward_flat': return `Coin \u0026 EXP +${pot.value}`;
    case 'score_bonus': return `Score Bonus +${pot.value} %`;
    case 'fever_bonus': return `Fever Score +${pot.value} %`;
    case 'time_bonus': return `Game Time +${pot.value} s`;
    default: return '';
  }
};
